import Endereco from './endereco.model';
import { utils } from '../../../../util/utils';
import { camposSocioStore } from '../../../../stateManagement/camposSocio';
import { camposEnderecoStore } from '../../../../stateManagement/camposEndereco';
import { opcoesRegimeCasamento } from '../../../../util/optionsEstaticos.utils';
import { gerarUUID } from '../../../../util/uuid.utils';

/**
 * Modelo de objeto para Dados do sócio do eLegalize;
 * @class DadosSocio
 * @namespace DadosSocio
 */
export default class DadosSocio {
  /**
   * Modelo para dados do Sócio no eLegalize
   * @param {String} nome - Nome
   * @param {String} cpf - CPF
   * @param {String} nascimento - Data de nascimento
   * @param {String} identidade - Número do RG
   * @param {String} orgaoEmissorIdentidade - Orgão emissor do RG
   * @param {String} emissaoIdentidade - Data de emissão do RG
   * @param {String} sexo - Sexo
   * @param {String} cor - Cor
   * @param {String} escolaridade - Escolaridade
   * @param {String} paisDeNascimento - País de nascimento
   * @param {String} nacionalidade - Nacionalidade
   * @param {String} naturalidade - Naturalidade
   * @param {String} municipioDeNascimento - Municipio de Nascimento
   * @param {String} estadoCivil - Estado civil
   * @param {String} regimeDoCasamento - Regime de casamento
   * @param {Boolean} isAdministrador - Sócio é do tipo administrador
   * @param {Boolean} isResponsavelCnpj - Sócio é do tipo responsavel pelo CNPJ
   * @param {EnderecoModel} endereço - Endereço
   * @param {String} emailContato - e-mail
   * @param {String} whatsappContato - Whatsapp
   * @param {Number} id - Id do sócio
   * @param {String} uuid - Id do sócio
   * @constructor
   */
  constructor(
    nome = '',
    cpf = '',
    nascimento = '',
    identidade = '',
    orgaoEmissorIdentidade = '',
    emissaoIdentidade = '',
    sexo = '',
    cor = '',
    escolaridade = '',
    paisDeNascimento = '',
    nacionalidade = '',
    naturalidade = '',
    municipioDeNascimento = '',
    estadoCivil = '',
    regimeDoCasamento = '',
    isAdministrador = false,
    isResponsavelCnpj = false,
    endereco = new Endereco(),
    emailContato = '',
    whatsappContato = '',
    id = null,
    uuid = gerarUUID()
  ) {
    this.nome = nome;
    this.cpf = cpf;
    this.nascimento = nascimento;
    this.identidade = identidade;
    this.orgaoEmissorIdentidade = orgaoEmissorIdentidade;
    this.emissaoIdentidade = emissaoIdentidade;
    this.isAdministrador = isAdministrador;
    this.isResponsavelCnpj = isResponsavelCnpj;
    this.endereco = endereco;
    this.emailContato = emailContato;
    this.whatsappContato = whatsappContato;
    this.sexo = this.getId(sexo);
    this.cor = this.getId(cor);
    this.escolaridade = this.getId(escolaridade);
    this.paisDeNascimento = this.getId(paisDeNascimento);
    this.nacionalidade = this.getId(nacionalidade);
    this.naturalidade = this.getId(naturalidade);
    this.municipioDeNascimento = municipioDeNascimento;
    this.estadoCivil = this.getId(estadoCivil);
    this.regimeDoCasamento = this.getId(regimeDoCasamento);
    this.id = id;
    this.uuid = this.id != null? null : uuid;
  }

  /**
   * Sendo parametro um numero retorna ele;
   * Sendo Objeto, retorna a propriedade ID do mesmo.
   * @param {*} dado - Dado base
   */
  getId(dado) {
    if (Number(dado) != 'NaN') {
      return dado;
    }
    return dado.id;
  }

  /**
   * Atualiza modelo com dados válidos de um objeto com dados de
   * formulário;
   * @param {Object} formData Objeto com dados do formulário
   */
  updateDados(formData) {
    for (let propriedade in formData) {
      if (!Object.keys(formData).includes(propriedade)) {
        console.warn(`WARN: Propriedade "${propriedade}" não é valido para modelo`);
        continue;
      }
      this[propriedade] = formData[propriedade];
    }
  }

  /**
   * Formata e trata, se necessário dados do modelo para ser inserido no body de um request
   * @returns {Object} Objeto formatado para body da requisição;
   */
  asRequestBody(cleanBody = true) {
    const nascimentoFormatado = utils.formatarDataAsStringToYyyyMmDd(this.nascimento);
    const emissaoFormatada = utils.formatarDataAsStringToYyyyMmDd(this.emissaoIdentidade);
    const body = {
      nome: this.nome,
      cpf: this.cpf,
      nascimento: nascimentoFormatado,
      identidade: this.identidade,
      orgaoEmissorIdentidade: this.orgaoEmissorIdentidade,
      emissaoIdentidade: emissaoFormatada,
      sexo: this.sexo,
      cor: this.cor,
      naturalidade: this.naturalidade,
      escolaridade: this.escolaridade,
      paisDeNascimento: this.paisDeNascimento,
      nacionalidade: this.nacionalidade,
      municipioDeNascimento: this.municipioDeNascimento,
      estadoCivil: this.estadoCivil,
      regimeDoCasamento: this.regimeDoCasamento,
      isAdministrador: this.isAdministrador,
      isResponsavelCnpj: this.isResponsavelCnpj,
      ...this.endereco.asRequestBody(),
      emailContato: this.emailContato,
      whatsappContato: this.whatsappContato,
      id: this.id,
      uuid: this.uuid
    };

    if(cleanBody) {
      this.cleanBody(body);
    }
    
    return body;
  }

  /**
   * Limpa do objeto campos vazios antes de enviar o request para o back;
   * @param {Object} body - body do request
   */
  cleanBody(body) {
    for (const prop in body) {
      if (body[prop] === '' || body[prop] === null || (Array.isArray(body[prop]) && body[prop].length == 0)) {
        delete body[prop];
      }
    }
  }

  /**
   * Formata e trata, se necessário, dados da resposta para o modelo
   */
  asModel(response) {
    this.endereco.asModel(response);
    this.cpf = response.cpf;
    this.emissaoIdentidade = utils.formatarDataParaExibicaoNoDataTable(response.emissaoIdentidade);
    this.identidade = response.identidade;
    this.isAdministrador = response.isAdministrador;
    this.isResponsavelCnpj = response.isResponsavelCnpj;
    this.municipioDeNascimento = response.municipioDeNascimento;
    this.nascimento = utils.formatarDataParaExibicaoNoDataTable(response.nascimento);
    this.nome = response.nome;
    this.orgaoEmissorIdentidade = response.orgaoEmissorIdentidade;
    this.emailContato = response.emailContato;
    this.whatsappContato = response.whatsappContato;
    this.id = response.id;
    this.setSexo(response.sexo);
    this.setEscolaridade(response.escolaridade);
    this.setEstadoCivil(response.estadoCivil);
    this.setPaisDeNascimento(response.paisDeNascimento);
    this.setNacionalidade(response.nacionalidade);
    this.setNaturalidade(response.naturalidade);
    this.setRegimeDoCasamento(response.regimeDoCasamento);
    this.setCor(response.cor);
  }

  setSexo(sexo) {
    const tiposSexo = camposSocioStore.tiposSexo;
    this.setFromResponse(tiposSexo, sexo, 'sexo');
  }

  setEscolaridade(escolaridade) {
    const listagemEscolaridades = camposSocioStore.tiposEscolaridade;
    this.setFromResponse(listagemEscolaridades, escolaridade, 'escolaridade');
  }

  setEstadoCivil(estadoCivil) {
    const listagemEstadosCivil = camposSocioStore.tiposEstadoCivil;
    this.setFromResponse(listagemEstadosCivil, estadoCivil, 'estadoCivil');
  }

  setPaisDeNascimento(pais) {
    const listagemPaises = camposEnderecoStore.paises;
    this.setFromResponse(listagemPaises, pais, 'paisDeNascimento');
  }

  setNacionalidade(nacionalidade) {
    const listagemPaises = camposEnderecoStore.paises;
    this.setFromResponse(listagemPaises, nacionalidade, 'nacionalidade');
  }

  setNaturalidade(naturalidade) {
    const listagemEstados = camposEnderecoStore.estados;
    this.setFromResponse(listagemEstados, naturalidade, 'naturalidade');
  }

  setRegimeDoCasamento(regimeDoCasamento) {
    const listagemRegimes = opcoesRegimeCasamento;
    this.setFromResponse(listagemRegimes, regimeDoCasamento, 'regimeDoCasamento');
  }

  setCor(cor) {
    const listagemRacas = camposSocioStore.tiposRaca;
    this.setFromResponse(listagemRacas, cor, 'cor');
  }

  /**
   * Encontra um valor em uma lista com base no dado da resposta.
   *
   * @param {Array<{label: string, value: any}>} lista A lista para pesquisar.
   * @param {string} dadoDaResponse O dado a ser pesquisado no objeto de resposta.
   * @param {string} propriedadeAlvo A propriedade do objeto 'this' para atribuir o valor encontrado.
   */
  setFromResponse(lista, dadoDaResponse, proprieadeAlvo) {
    if (!dadoDaResponse) return;

    const itemEncontrado = lista.find((item) => item.label.toLowerCase() === dadoDaResponse.toLowerCase());
    if (!!itemEncontrado) {
      this[proprieadeAlvo] = itemEncontrado.value;
    }
  }
}
